import { useMemo, useState } from "react";

import { IWellnessPlanCatalogItem } from "../services/catalog-items/catalog-items.interface";
import { useWellnessPlanCatalog } from "../services/catalog-items/catalog-items.service";

export interface CategoryItem {
  name: string;
  quantity: number;
  discount?: string | number | null;
  unlimited?: boolean;
}

export interface FlattenedPlanData {
  planName: string;
  price: number;
  description: string;
  term: string;
  planId: number;
  planIdMonthly?: number;
  services?: { [categoryName: string]: CategoryItem[] };
  [categoryName: string]:
    | CategoryItem[]
    | string
    | number
    | boolean
    | { [key: string]: CategoryItem[] }
    | undefined;
  planType: string;
  ageGroup: string;
  planCode: string;
  monthlyPrice?: number;
  oneTimeDownpayment?: number;
  scratchpayPlanId?: number;
  preferredBillingCycle?: "perYear" | "perMonth";
  fromScratchPayRenewal?: boolean;
  prequalStatus?: "ELIGIBLE" | "INELIGIBLE";
}

export interface IPlanOption {
  planName: string;
  planValue: string;
  planPrice: number;
  defaultPlan: boolean;
  planType: string;
  ageGroup: string;
  recommended: boolean;
  planCode: string;
}

export type planPricingType = Record<string, Record<"annual" | "monthly", number>>;

const EMPTY_FLATTENED_PLAN: FlattenedPlanData = {
  planName: "",
  price: 0,
  description: "",
  term: "",
  planId: 0,
  services: {},
  planType: "",
  ageGroup: "",
  planCode: "",
  monthlyPrice: 0,
  oneTimeDownpayment: 0,
};

function derivePlanTypeString(plan: IWellnessPlanCatalogItem): string {
  if (plan.planType === "Base") return "base";
  return `${plan.planType.toLowerCase()}_${(plan.ageGroup ?? "adult").toLowerCase()}`;
}

function derivePlanKey(plan: IWellnessPlanCatalogItem): string {
  if (plan.planType === "Base") return "basePlan";
  return `${plan.planType.toLowerCase()}_${(plan.ageGroup ?? "adult").toLowerCase()}`;
}

function planToFlattenedData(plan: IWellnessPlanCatalogItem): FlattenedPlanData {
  const services: { [categoryName: string]: CategoryItem[] } = {};

  plan.benefits.forEach((benefit) => {
    const items: CategoryItem[] = benefit.items
      .filter((item) => item.unlimited || (item.quantity ?? 0) !== 0 || (item.discount ?? 0) > 0)
      .map((item) => ({
        name: item.name,
        quantity: item.unlimited ? -1 : (item.quantity ?? 0),
        ...(item.discount !== undefined && { discount: item.discount }),
        ...(item.unlimited !== undefined && { unlimited: item.unlimited }),
      }));

    if (items.length > 0) {
      services[benefit.category] = items;
    }
  });

  const monthlyPlanId = plan.monthly?.planId;

  return {
    planName: plan.displayName,
    price: plan.annual?.price ?? 0,
    description: plan.description,
    term: "annual",
    planId: plan.annual?.planId ?? 0,
    planType: derivePlanTypeString(plan),
    ageGroup: plan.ageGroup ?? "",
    planCode: plan.displayName,
    ...(Object.keys(services).length > 0 && { services }),
    ...(plan.monthly?.price !== undefined && { monthlyPrice: plan.monthly.price }),
    ...(plan.monthly?.enrollmentFee?.price !== undefined && { oneTimeDownpayment: plan.monthly.enrollmentFee.price }),
    ...(monthlyPlanId !== undefined && { planIdMonthly: monthlyPlanId }),
    ...(monthlyPlanId !== undefined && { scratchpayPlanId: monthlyPlanId }),
  };
}

const useWellnessPlans = () => {
  const [selectedPlan, setSelectedplan] = useState<string>("");
  const { fetchCatalogItems, catalogPlans, loading, error, called } = useWellnessPlanCatalog();

  const annualAvailablePlans = useMemo<IPlanOption[]>(() =>
    (catalogPlans ?? []).map((plan) => ({
      planName: plan.displayName,
      planValue: derivePlanKey(plan),
      planPrice: plan.annual?.price ?? 0,
      defaultPlan: plan.recommended,
      planType: derivePlanTypeString(plan),
      ageGroup: plan.ageGroup ?? "",
      recommended: plan.recommended,
      planCode: plan.displayName,
    })),
  [catalogPlans]);

  const planPricing = useMemo<planPricingType>(() => {
    const result: planPricingType = {};
    (catalogPlans ?? []).forEach((plan) => {
      result[derivePlanKey(plan)] = {
        annual: plan.annual?.price ?? 0,
        monthly: plan.monthly?.price ?? 0,
      };
    });
    return result;
  }, [catalogPlans]);

  const annualSegregatedPlanData = useMemo(() =>
    (catalogPlans ?? []).length > 0 ? { _loaded: true } : {},
  [catalogPlans]);

  const monthlySegregatedPlanData = useMemo(() =>
    (catalogPlans ?? []).length > 0 ? { _loaded: true } : {},
  [catalogPlans]);

  const getCurrentPlanData = (planKey: string): FlattenedPlanData => {
    const plan = (catalogPlans ?? []).find((p) => derivePlanKey(p) === planKey);
    if (!plan) return EMPTY_FLATTENED_PLAN;
    return planToFlattenedData(plan);
  };

  const getPlanDataByPlanId = (planId: number, planType?: string): FlattenedPlanData | null => {
    const plans = catalogPlans ?? [];

    let plan = plans.find(
      (p) => p.annual?.planId === planId || p.monthly?.planId === planId,
    );

    if (!plan && planType) {
      plan = plans.find(
        (p) => derivePlanTypeString(p).toLowerCase().includes(planType.toLowerCase()),
      );
    }

    return plan ? planToFlattenedData(plan) : null;
  };

  const getPlanDataByScratchpayPlanId = (scratchpayPlanId: number): FlattenedPlanData | null => {
    const plan = (catalogPlans ?? []).find(
      (p) => p.monthly?.planId === scratchpayPlanId,
    );
    return plan ? planToFlattenedData(plan) : null;
  };

  return {
    selectedPlan,
    setSelectedplan,
    fetchCatalogItems,
    catalogItems: catalogPlans,
    loading,
    error,
    called,
    annualSegregatedPlanData,
    monthlySegregatedPlanData,
    annualAvailablePlans,
    monthlyAvailablePlans: annualAvailablePlans,
    getCurrentPlanData,
    getPlanDataByPlanId,
    getPlanDataByScratchpayPlanId,
    planPricing,
  };
};

export default useWellnessPlans;
